import { AnyAction, Middleware } from 'redux';
import { AppState } from '../rootReducer';
import { AGREEMENT, CORE, AUTH } from 'core/datasets/action';
import { b64toBlob, handleGraphQLRequest } from './helpers/coreMiddlewareHelpers';
import { coreActions } from '../actions/coreActions';
import { NotificationAlert } from 'core/classes/NotificationAlert';
import { PAGE_URL } from 'core/datasets/page_url';
import { fetchRefreshTokenGraphQL } from 'core/config/gql/fetchRefreshToken';
import axios from 'axios';
import DashboardNotification from 'core/classes/DashboardNotification';
import moment from 'moment';
import { isEmpty } from 'lodash';

const coreMiddleware: Middleware =
    ({ getState, dispatch }) =>
    (next) =>
    async (action) => {
        next(action);

        const state: AppState = getState();

        const refreshAndReturnToken = async () => {
            const tokenData = await fetchRefreshTokenGraphQL(state.auth.refresh_token);

            if (!tokenData || isEmpty(tokenData)) {
                throw new Error('Empty refresh token data');
            }

            dispatch({
                type: AUTH.TOKENS_CHANGED,
                payload: {
                    token: tokenData.token,
                    refresh_token: tokenData.refresh_token,
                },
            });
            return tokenData.token;
        };

        switch (action.type) {
            case CORE.MAKE_GRAPHQL_REQUEST: {
                try {
                    const now = moment().unix();
                    let token = state.auth.token;

                    if (state.auth.expiresAt < now) {
                        token = await refreshAndReturnToken();
                    }

                    const resultAction: AnyAction[] = await handleGraphQLRequest(action, token);
                    resultAction.forEach((action) => dispatch(action));
                } catch (error) {
                    if (axios.isAxiosError(error) && error.response?.status === 401) {
                        await refreshAndReturnToken();

                        // Retry the request
                        dispatch({
                            type: CORE.MAKE_GRAPHQL_REQUEST,
                            payload: action.payload,
                        });

                        return;
                    }

                    if (error instanceof Error) {
                        const alert = new NotificationAlert({ message: error.message, level: NotificationAlert.ERROR });
                        dispatch(coreActions.notification.set(alert.all));
                    }
                }
                break;
            }
            case CORE.DEVICE_GLOBAL_ERROR: {
                const { errorMessage, errorData, id } = action.payload;
                console.error(`LARVA Device error: `, errorMessage, errorData);

                if (id) {
                    dispatch({
                        type: CORE.DEVICE_ADD_ERROR,
                        payload: {
                            fullId: id,
                            error: errorMessage,
                        },
                    });
                }
                break;
            }
            case CORE.ERROR_LOGGED: {
                console.error(action.payload);
                break;
            }
            case CORE.CONVERT_BASE64_TO_URL: {
                const { b64Data, mimeType, callbackAction } = action.payload;
                const url = b64toBlob(b64Data, mimeType);
                dispatch({ type: callbackAction, payload: url });
                break;
            }
            case AGREEMENT.AGREEMENT_SELECTED: {
                if (!action.payload) {
                    const notification = new DashboardNotification({
                        message: 'rentalObject.agreementStatus.missingActiveAgreements',
                        level: NotificationAlert.WARNING,
                        path: PAGE_URL.RENTAL_AGREEMENTS,
                    });
                    dispatch(coreActions.dashboardNotifications.push(notification.all));
                }
                break;
            }
            case AGREEMENT.INIT_AGREEMENTS_FETCHING: {
                dispatch(coreActions.dashboardNotifications.clear());
                break;
            }
            default: {
                break;
            }
        }
    };

export default coreMiddleware;
